import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ContactCallout } from '../components/ContactCallout';
import type { ServiceDetail } from '../data/services';
type ServicePageProps = {
  service: ServiceDetail;
};
const accentClasses = {
  teal: 'bg-[#dceee7] text-[#286151] border-[#9bc6b5]',
  ochre: 'bg-[#f4e7c3] text-[#8b5c18] border-[#dfc17c]',
  coral: 'bg-[#f4dfd8] text-[#a24e38] border-[#dca18f]'
};
export function ServicePage({
  service
}: ServicePageProps) {
  const Icon = service.icon;
  return <>
      <main data-id="element-241" data-mp="34z92cy2i271r">
        <section className="bg-[#f8f7f3] px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-24" aria-labelledby="service-heading" data-id="element-242" data-mp="34zsjickbsreq">
          <div className="mx-auto max-w-7xl" data-id="element-243" data-mp="3e0qrf5xvxj2l">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 transition-colors hover:text-[#172820]" data-id="element-244" data-mp="3e1iaxie7jqci">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-245" data-mp="9xgggoevh6ra" />
              Back to home
            </Link>
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="mt-14 grid gap-10 lg:grid-cols-[1fr_0.7fr] lg:items-end" data-id="element-246" data-mp="dxqwyv0ajfke">
              <div data-id="element-247" data-mp="wv0fm9xskp0l">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#286151]" data-id="element-248" data-mp="wu8l72fxkxbw">
                  {service.eyebrow}
                </p>
                <h1 id="service-heading" className="mt-5 max-w-3xl text-5xl font-semibold leading-[0.98] tracking-[-0.045em] text-[#172820] sm:text-6xl" style={{
                fontFamily: 'var(--font-display)'
              }} data-id="element-249" data-mp="16mece9yczaq1">
                  {service.title}
                </h1>
                <p className="mt-7 max-w-2xl text-lg leading-8 text-stone-600" data-id="element-250" data-mp="qtu2dkkhzcqr">
                  {service.introduction}
                </p>
              </div>
              <div className={`border p-7 ${accentClasses[service.accent]}`} data-id="element-251" data-mp="10mroe0op7ju4">
                <Icon className="h-7 w-7" aria-hidden="true" data-id="element-252" data-mp="vv01q18nxy99" />
                <p className="mt-8 text-xs font-bold uppercase tracking-[0.16em]" data-id="element-253" data-mp="19moabujpe4lh">
                  Designed for traction
                </p>
                <p className="mt-3 text-lg font-medium leading-7" data-id="element-254" data-mp="14mjtg3evv7uq">
                  Clear decisions, visible workflows, and a CRM your team can
                  trust.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-white px-5 py-20 sm:px-8 lg:px-10" aria-labelledby="outcomes-heading" data-id="element-255" data-mp="3lbxloy3nyj9b">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-256" data-mp="3ge5uk6auf166">
            <div data-id="element-257" data-mp="1vw4rn9klc3wz">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#286151]" data-id="element-258" data-mp="1vvlmktg1ha0d">
                What changes
              </p>
              <h2 id="outcomes-heading" className="mt-4 text-3xl font-semibold tracking-tight text-[#172820] sm:text-4xl" style={{
              fontFamily: 'var(--font-display)'
            }} data-id="element-259" data-mp="1m5a5s6b9b6c2">
                The outcome is clarity your team can use.
              </h2>
            </div>
            <ul className="space-y-5 border-t border-stone-200" data-id="element-260" data-mp="1h7qqd32tonij">
              {service.outcomes.map(outcome => <li key={outcome} className="flex gap-4 border-b border-stone-200 py-5 text-lg leading-7 text-stone-700" data-id="element-261" data-mp="138ir6a6ntfec">
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#dceee7] text-[#286151]" data-id="element-262" data-mp="2xkysqxoig5cv">
                    <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-263" data-mp="32hfpgg9rzs1k" />
                  </span>
                  {outcome}
                </li>)}
            </ul>
          </div>
        </section>

        <section className="border-y border-stone-200 bg-[#f8f7f3] px-5 py-20 sm:px-8 lg:px-10" aria-labelledby="delivery-heading" data-id="element-264" data-mp="xgd4b6v10alm">
          <div className="mx-auto max-w-7xl" data-id="element-265" data-mp="ogzqy33s71vm">
            <div className="max-w-xl" data-id="element-266" data-mp="jieso983as9t">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#286151]" data-id="element-267" data-mp="jjgacwjm2d0i">
                How we work
              </p>
              <h2 id="delivery-heading" className="mt-4 text-3xl font-semibold tracking-tight text-[#172820] sm:text-4xl" style={{
              fontFamily: 'var(--font-display)'
            }} data-id="element-268" data-mp="epllzft299v3">
                A focused, practical delivery process.
              </h2>
            </div>
            <div className="mt-12 grid gap-px overflow-hidden border border-stone-200 bg-stone-200 lg:grid-cols-3" data-id="element-269" data-mp="3rz9j2p7pak70">
              {service.delivery.map(phase => <article key={phase.step} className="bg-[#f8f7f3] p-6 sm:p-8" data-id="element-270" data-mp="2cf0k5kvdazid">
                  <p className="text-sm font-bold text-[#286151]" data-id="element-271" data-mp="2ch3j4uei02z8">
                    {phase.step}
                  </p>
                  <h3 className="mt-10 text-xl font-semibold tracking-tight text-[#172820]" style={{
                fontFamily: 'var(--font-display)'
              }} data-id="element-272" data-mp="2lfp0im88hbyf">
                    {phase.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-stone-600" data-id="element-273" data-mp="1xqmez3ahayyn">
                    {phase.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-16 sm:px-8 lg:px-10" data-id="element-274" data-mp="15y5bgo585vis">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-5 border-l-2 border-[#5a9c82] pl-5 sm:flex-row sm:items-center" data-id="element-275" data-mp="1a0jsd2pc2l7f">
            <p className="max-w-2xl text-base leading-7 text-stone-600" data-id="element-276" data-mp="2daeccstwwimq">
              Every engagement is scoped around the systems and decisions that
              matter to your team.
            </p>
            <Link to="/" className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[#286151] hover:text-[#172820]" data-id="element-277" data-mp="2n0zbqihetr6p">
              See all services{' '}
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-278" data-mp="1uexre3p1qj2e" />
            </Link>
          </div>
        </section>
      </main>
      <ContactCallout title={`Discuss your ${service.shortTitle.toLowerCase()} project.`} description="Share the context, the systems involved, and what you want to make easier. I will reply with a sensible next step." data-id="element-279" data-mp="1zc6b48puev5r" />
    </>;
}